import { getRegisteredDomain, urlToDomain } from "./domains";
import { serializeStructuredStackTrace, StructuredStackTrace } from "./serializeStructuredStackTrace";

type PrepareStackTraceFn = NonNullable<(typeof Error)["prepareStackTrace"]>;
// export type StructuredStackTrace = Parameters<PrepareStackTraceFn>[1];
export type CallSite = NonNullable<StructuredStackTrace[0]>;
export class StackArrayGenerator extends Error {
	readonly structuredStackTrace: StructuredStackTrace;

	stackTraceLimit: number;

	constructor({depth = 100, topFnToIgnore=StackArrayGenerator}: {depth?: number, topFnToIgnore?: Function}) {
		super();
		this.stackTraceLimit = depth;
		const originalPrepareStackTrace = Error.prepareStackTrace!;
		const originalLimit = Error.stackTraceLimit;
		try {
			Error.stackTraceLimit = depth;
			this.stackTraceLimit = depth;
			Error.prepareStackTrace = function (error, structuredStackTrace) {
				return structuredStackTrace;
			};
			Error.captureStackTrace(this, topFnToIgnore);
			this.structuredStackTrace = this.stack as unknown as StructuredStackTrace;
		} finally {
			Error.prepareStackTrace = originalPrepareStackTrace;
			Error.stackTraceLimit = originalLimit;
		}
	}
}

export class StackTextGenerator extends Error {
	public readonly stackText: string;

	constructor({depth = 100, topFnToIgnore=StackTextGenerator}: {depth?: number, topFnToIgnore?: Function}) {
		super();
		const originalLimit = Error.stackTraceLimit;
		try {
			Error.stackTraceLimit = depth;
			Error.captureStackTrace(this, topFnToIgnore);
			this.stackText = this.stack ?? "";
			if (this.stackText.startsWith("Error\n")) {
				this.stackText = this.stackText.slice(6);
			}
		} finally {
			Error.stackTraceLimit = originalLimit;
		}
	}
}

export const getDomainsOfCallers = ({stackArray}: {stackArray: ReturnType<typeof serializeStructuredStackTrace>}) => ([
	...new Set<string>(
		stackArray
			.map(({fileName}) => urlToDomain(fileName) )
			.filter((hostname) => hostname != null)
	)]
);

export const getCodeLocation = (
	topFnToIgnore: Function | undefined,
) => {
	const {structuredStackTrace} = new StackArrayGenerator({topFnToIgnore});
	const {stackText} = new StackTextGenerator({topFnToIgnore});
	const stackArray = serializeStructuredStackTrace(structuredStackTrace);
	let windowLocationHref: string = "";
	try {
		windowLocationHref = window?.location?.href ?? "";
	} catch {}
	const firstPartyRegisteredDomain = getRegisteredDomain(windowLocationHref);
	const thirdPartyDomainsOnCallStackBottomFirst = getDomainsOfCallers({stackArray})
		.filter((hostname) => getRegisteredDomain(hostname) !== firstPartyRegisteredDomain);
	return {stackText, stackArray, windowLocationHref, thirdPartyDomainsOnCallStackBottomFirst}
}

export type CodeLocation = ReturnType<typeof getCodeLocation>;
export const hasCodeLocation = (obj: unknown): obj is CodeLocation =>
	typeof obj === "object" && obj != null &&
	"stackText" in obj && "stackArray" in obj && "windowLocationHref" in obj && "thirdPartyRegisteredDomainsInCallStack" in obj &&
	Array.isArray((obj as any).stackArray) &&
	typeof obj.stackText === "string" &&
	typeof obj.windowLocationHref === "string" &&
	Array.isArray(obj.thirdPartyRegisteredDomainsInCallStack);
